import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AccountTree as WorkflowIcon,
  CheckCircle as CheckIcon,
  Groups as PartiesIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';
import { BUSINESS } from '../../constants/business';

const phases = [
  {
    phase: 'Phase 1: File Opening',
    window: 'Days 1-3 after contract',
    owner: 'Title agent / processor',
    tasks: [
      'Receive fully executed contract and confirm parties, property, and key dates',
      'Open the title order and assign a file number',
      'Order the title search and tax search',
      'Send introduction letters to buyer, seller, attorneys, agents, and lender',
      'Collect buyer and seller information sheets (marital status, payoff info, forwarding addresses)',
      'Send wire fraud warning notice to all parties with the first communication',
    ],
  },
  {
    phase: 'Phase 2: Title Production',
    window: 'Days 3-10',
    owner: 'Title agent / examiner',
    tasks: [
      'Receive and examine search results; identify liens, judgments, and exceptions',
      'Issue the title commitment to the lender and attorneys',
      'Order municipal certifications (tax, water/sewer, CCO where required by the municipality)',
      'Flag curative items early and assign responsibility for each (seller payoff, discharge, affidavit)',
      'Order survey if required; review for encroachments when received',
      'Order payoff statements and association (HOA/condo) letters',
    ],
  },
  {
    phase: 'Phase 3: Clearing to Close',
    window: 'Days 10-30',
    owner: 'Processor + attorneys',
    tasks: [
      'Track curative items to completion; update the commitment as items clear',
      'Coordinate with the lender on closing conditions and CD/settlement statement preparation',
      'Reconcile lender figures, payoffs, adjustments, and commissions on the settlement statement',
      'Confirm homeowner insurance binder, smoke certificate, and any municipal requirements',
      'Schedule the closing date, time, and location with all parties',
      'Confirm buyer cash-to-close and deliver verified wiring instructions through a secure channel',
    ],
  },
  {
    phase: 'Phase 4: Closing Day',
    window: 'Closing day',
    owner: 'Settlement agent',
    tasks: [
      'Verify receipt of all funds as collected/good funds before the table',
      'Review and execute the deed, affidavit of title, and transfer documents',
      'Execute lender package and settlement statement with all parties',
      'Disburse only against the final signed settlement statement',
      'Collect keys/possession items per the contract terms',
      'Provide copies of the full signed package to all parties',
    ],
  },
  {
    phase: 'Phase 5: Post-Closing',
    window: 'Days 1-45 after closing',
    owner: 'Post-closer',
    tasks: [
      'Record the deed and mortgage with the county clerk without delay',
      'Confirm payoff lines are satisfied and discharges are recorded; follow up at 30/60/90 days',
      'Issue final title policies to owner and lender',
      'Return recorded originals to the appropriate parties',
      'Complete the disbursement ledger and reconcile the file to zero',
      'Archive the file per retention policy and close it in the production system',
    ],
  },
];

const responsibilityMatrix = [
  { task: 'Title search and commitment', titleAgent: true, attorney: false, lender: false, agent: false },
  { task: 'Curative work (payoffs, discharges)', titleAgent: true, attorney: true, lender: false, agent: false },
  { task: 'Contract contingencies (inspection, attorney review)', titleAgent: false, attorney: true, lender: false, agent: true },
  { task: 'Loan conditions and Closing Disclosure timing', titleAgent: false, attorney: false, lender: true, agent: false },
  { task: 'Settlement statement preparation', titleAgent: true, attorney: true, lender: true, agent: false },
  { task: 'Closing scheduling and party coordination', titleAgent: true, attorney: true, lender: false, agent: true },
  { task: 'Recording and final policy issuance', titleAgent: true, attorney: false, lender: false, agent: false },
];

const ClosingWorkflowTemplate: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Closing Coordination Workflow for NJ Title Professionals | MAD Title Agency</title>
        <meta
          name="description"
          content="Step-by-step New Jersey closing coordination workflow: file opening, title production, clearing to close, closing day, and post-closing, with a responsibility matrix."
        />
        <meta
          name="keywords"
          content="closing workflow template, closing coordination NJ, title agency workflow, settlement process checklist, post-closing checklist"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Closing Coordination Workflow
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#0f172a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Resource"
                sx={{ backgroundColor: '#f59e0b', color: 'white', mb: 2 }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
              >
                Closing Coordination Workflow
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                A five-phase workflow template for coordinating New Jersey closings between all parties
              </Typography>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ backgroundColor: '#f59e0b', '&:hover': { backgroundColor: '#d97706' } }}
              >
                Print Workflow
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <WorkflowIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  5 Phases, ~45 Days
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Contract to closed-and-archived file
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Intro */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            How to Use This Workflow
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
            Most closing problems are coordination problems: a payoff ordered late, a municipal
            certification nobody requested, wiring instructions confirmed at the last minute. This
            template breaks a standard New Jersey residential closing into five phases with clear
            task ownership, so nothing waits on an unasked question. Adapt the day ranges to your
            contract timeline; a typical purchase runs 30 to 45 days.
          </Typography>
          <Alert severity="info">
            <Typography variant="body2">
              New Jersey practice varies by county and by whether attorneys conduct the closing
              (common in North Jersey) or the title agency conducts settlement (common in South
              Jersey). Adjust the responsibility column to match your transaction's structure.
            </Typography>
          </Alert>
        </Box>

        {/* Phases */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            The Five Phases
          </Typography>
          {phases.map((phase, index) => (
            <Card key={index} sx={{ mb: 4, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e3a8a' }}>
                    {phase.phase}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={phase.window} color="primary" />
                    <Chip label={phase.owner} variant="outlined" />
                  </Box>
                </Box>
                <List dense>
                  {phase.tasks.map((task, i) => (
                    <ListItem key={i} alignItems="flex-start">
                      <ListItemIcon sx={{ mt: 0.5 }}>
                        <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={task} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Responsibility matrix */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Who Owns What
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
            <PartiesIcon sx={{ fontSize: 20, mr: 0.5, verticalAlign: 'text-bottom' }} />
            A quick-reference matrix for the four coordinating parties in a typical NJ purchase:
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1e3a8a' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Task</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Title Agent</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Attorneys</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Lender</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">RE Agents</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responsibilityMatrix.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' } }}>
                    <TableCell>{row.task}</TableCell>
                    <TableCell align="center">{row.titleAgent ? <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} /> : ''}</TableCell>
                    <TableCell align="center">{row.attorney ? <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} /> : ''}</TableCell>
                    <TableCell align="center">{row.lender ? <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} /> : ''}</TableCell>
                    <TableCell align="center">{row.agent ? <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} /> : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Related resources */}
        <Box sx={{ mb: 8 }}>
          <Alert severity="info">
            <Typography variant="body2">
              Pair this workflow with our{' '}
              <Link to="/resources/nj-recording-requirements" style={{ fontWeight: 600 }}>
                NJ Recording Requirements by County
              </Link>{' '}
              for Phase 5 and the{' '}
              <Link to="/resources/middlesex-county-closing-checklist" style={{ fontWeight: 600 }}>
                Middlesex County Closing Checklist
              </Link>{' '}
              for the consumer-facing view. Workflow questions? Email{' '}
              <strong>{BUSINESS.contact.email}</strong>.
            </Typography>
          </Alert>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="Closing Coordination Workflow"
          resourceType="professional"
          formType="consultation"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Need a Title Partner Who Runs This Playbook?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              We coordinate closings across Middlesex County and all of New Jersey.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Contact Us
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources/nj-recording-requirements"
                sx={{ borderColor: '#1e3a8a', color: '#1e3a8a', px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                Recording Requirements
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ClosingWorkflowTemplate;
